"use client";

import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useSession } from "next-auth/react";
import { Globe } from "lucide-react";
import { SITE_LANG_OPTIONS } from "@/constants/common";

const getCookie = (name: string) => {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
};

const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const SiteLangToggle = () => {
  const [languageOptions, setLanguageOptions] =
    useState<{ name: string; iso3: string }[]>(SITE_LANG_OPTIONS);
  const [selectedLang, setSelectedLang] = useState("eng");
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: session } = useSession();
  const entity_id = session?.user?.entity_id || 0;

  useEffect(() => {
    fetchUserLanguage();
    // fetchLanguageOptions();
  }, [selectedLang]);

  const fetchLanguageOptions = async () => {
    try {
      const response = await fetch("/next/api/sitelang/options");
      if (response.ok) {
        const data = await response.json();
        setLanguageOptions(data);

        // Save on cookies the options of language
        setCookie("site_lang_options", JSON.stringify(data), 30);
      } else {
        console.error("Error fetching language options");
      }
    } catch (error) {
      console.error("Network error:", error);
    }
  };

  const fetchUserLanguage: () => Promise<void> = async () => {
    const cookieLang = getCookie("site_lang");

    if (cookieLang) {
      setSelectedLang(cookieLang);
    } else if (entity_id > 0) {
      deleteCookie("nav_strings");
      const siteLangResponse = await fetch(`/next/api/sitelang/user?entity_id=${entity_id}`);
      if (siteLangResponse.ok) {
        const { site_lang } = await siteLangResponse.json();
        const lang = site_lang?.length > 0 ? site_lang[0].site_lang : "eng";
        setCookie("site_lang", lang, 30);
        fetchNavStrings(lang);
      }
    }
  };

  const fetchNavStrings = async (lang: string) => {
    try {
      const navStringsResponse = await fetch(`/next/api/sitelang/nav_strings?lang=${lang}`);
      if (navStringsResponse.ok) {
        const navStringsData = await navStringsResponse.json();

        const formattedNavStrings = navStringsData.reduce(
          (acc: Record<string, string>, item: { sid: string; string: string }) => {
            acc[item.sid] = item.string;
            return acc;
          },
          {}
        );
        setCookie("nav_strings", JSON.stringify(formattedNavStrings), 1);
      } else {
        console.error("Failed to fetch navigation strings");
      }
    } catch (error) {
      console.error("Error fetching navigation strings:", error);
    }
  };

  const changeLanguage = async (lang: string) => {
    deleteCookie("site_lang");
    setCookie("site_lang", lang, 365);
    setSelectedLang(lang);
    deleteCookie("nav_strings");
    setMenuOpen(false);

    if (entity_id > 0) {
      try {
        const response = await fetch("/next/api/sitelang/change", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lang_code: lang, entityId: entity_id }),
        });

        if (response.ok) {
          window.location.reload();
        } else {
          console.error("Error changing language");
        }
      } catch (error) {
        console.error("Network error:", error);
      }
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="flex items-center gap-2 p-2 border rounded-lg shadow-sm hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <Globe className="w-4 h-4 dark:text-white " />
        <span className="text-black dark:text-white">
          {languageOptions.find((lang) => lang.iso3 === selectedLang)?.iso3.toUpperCase()}
        </span>
        <ChevronDown className="w-4 h-4" />
      </button>
      {menuOpen && (
        <div className="absolute mt-2 border rounded-lg shadow-lg z-10 left-0 transform -translate-x-2/4 bg-white border-gray-300 dark:bg-gray-800 dark:border-gray-700 max-h-64 overflow-y-auto w-[175px]">
          {languageOptions
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((lang) => (
              <div
                key={lang.iso3}
                className="text-center gap-2 py-2 px-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => changeLanguage(lang.iso3)}
              >
                <span className="text-black dark:text-white">
                  ({lang.iso3.toUpperCase()}) {lang.name}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default SiteLangToggle;
